import { Popup, ScrollView, TextBox } from "devextreme-react";
import Button from "devextreme-react/button";
import {
  Column,
  DataGrid,
  Item as DataGridItem,
  FilterRow,
  Toolbar,
} from "devextreme-react/data-grid";
import {
  ButtonItem,
  Form,
  FormRef,
  GroupItem,
  Item,
} from "devextreme-react/form";
import notify from "../../lib/notify";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteSchema,
  deleteSchemaElem,
  downloadSchemaEvaluation,
  sendSchema,
  updateSchema,
} from "../../api/employeeEvaluation";
import { useScreenSize } from "../../utils/media-query";
import "./Sheets.scss";

export const Sheets = () => {
  const { t } = useTranslation();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [schema, setSchema] = useState<
    {
      id: number;
      name: string;
      sumScales: number;
      gradingMethod: string;
      rate: number;
      managerRate: number;
      elements: {
        id: number;
        positionName: string;
        details: string;
        scales: number;
        selfAssessment: any;
        menagerRating: any;
      }[];
    }[]
  >([]);
  const [popupVisibleAdd, setPopupVisibleAdd] = useState(false);
  const [popupVisibleUpdate, setPopupVisibleUpdate] = useState(false);
  const [schemaName, setSchemaName] = useState<string>("");
  const [scales, setScales] = useState<number>(0);
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const [schemaId, setSchemaId] = useState<number>(0);
  const [timeSlots, setTimeSlots] = useState([
    { positionName: "", details: "", scales: 0, id: 0 },
  ]);
  const [mobileFilter, setMobileFilter] = useState("");
  const formRef = useRef<FormRef | null>(null);

  const validateAll = () => {
    return formRef.current?.instance().validate();
  };

  useEffect(() => {
    fetchSchemaEvaluation();
  }, []);

  const fetchSchemaEvaluation = async () => {
    try {
      const response = await downloadSchemaEvaluation();
      if (response.isOk) {
        setSchema(response.data);
      } else {
        notify(t("An error occurred while processing your request."), "error");
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    }
  };

  const DeleteAllSchema = async (id: number) => {
    try {
      const response = await deleteSchema(id);
      if (response.isOk) {
        notify(t("Schema deleted succesfull."), "success");
        fetchSchemaEvaluation();
      } else {
        notify(t("Schema is not availble to remove."), "error");
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    }
  };

  const addSchema = async () => {
    try {
      const positions = timeSlots.map((slot) => slot.positionName);
      const scalesFromSlots = timeSlots.map((slot) => slot.scales);
      const detailsFromSlots = timeSlots.map((slot) => slot.details);

      const response = await sendSchema(
        schemaName,
        scales,
        selectedMethod,
        positions,
        detailsFromSlots,
        scalesFromSlots
      );

      if (response.isOk) {
        notify(t("Schema has been successfully saved."), "success");
        fetchSchemaEvaluation();
      } else {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error"
        );
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setPopupVisibleAdd(false);
    }
  };

  const updateSchemaSend = async () => {
    try {
      const updateEvaluationElem = timeSlots.map((slot) => ({
        positionName: slot.positionName,
        details: slot.details,
        scales: slot.scales,
        sectionId: slot.id ?? 0,
      }));

      const response = await updateSchema(
        schemaId,
        scales,
        schemaName,
        selectedMethod,
        updateEvaluationElem
      );

      if (response.isOk) {
        notify(t("Schema has been successfully saved."), "success");
        fetchSchemaEvaluation();
      } else {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error"
        );
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setPopupVisibleUpdate(false);
    }
  };

  const togglePopup = (data) => {
    setSchemaId(data.id);
    setSchemaName(data.name);
    setScales(data.sumScales);
    setSelectedMethod(data.gradingMethod);
    setTimeSlots(
      data.elements.map((el) => ({
        positionName: el.positionName,
        details: el.details,
        scales: el.scales,
        id: el.id,
      }))
    );
    setPopupVisibleUpdate(true);
  };

  const addPopup = () => {
    setSchemaName("");
    setScales(0);
    setSelectedMethod("");
    setTimeSlots([{ positionName: "", details: "", scales: 0, id: 0 }]);
    setPopupVisibleAdd(true);
  };

  const handleAddTimeSlot = () => {
    setTimeSlots((prevSlots) => [
      ...prevSlots,
      { positionName: "", details: "", scales: 0, id: 0 },
    ]);
  };

  const handleRemoveTimeSlot = async (index: number) => {
    const slotToRemove = timeSlots[index];

    if (slotToRemove.id !== 0) {
      try {
        await deleteSchemaElem(slotToRemove.id);
      } catch (error) {
        notify(`Błąd przy usuwaniu slotu: ${error}`, "error");
        return;
      }
    }
    setTimeSlots((prevSlots) => prevSlots.filter((_, i) => i !== index));
  };

  const hadnleDeleteSchema = (data: any) => {
    DeleteAllSchema(data.id);
  };

  const filteredSchema = useMemo(() => {
    const query = mobileFilter.trim().toLowerCase();
    if (!query) return schema;
    return schema.filter((s) => s.name?.toLowerCase().includes(query));
  }, [schema, mobileFilter]);

  const groupColCount = isMobile ? 1 : 3;

  const renderSlots = () => (
    <>
      {timeSlots.map((slot, index) => (
        <div key={slot.id || index} className="sheets-slot">
          <div className="sheets-slot-header">
            <div className="sheets-slot-title">
              {t("sheets.positionName")} {index + 1}
            </div>
            <Button
              icon="trash"
              type="danger"
              stylingMode="text"
              onClick={() => handleRemoveTimeSlot(index)}
            />
          </div>

          <Form colCount={groupColCount} style={{ marginTop: 12 }}>
            <Item
              label={{ text: t("sheets.positionName") }}
              editorType="dxTextBox"
              editorOptions={{
                value: slot.positionName,
                onValueChanged: (e: any) => {
                  const newSlots = [...timeSlots];
                  newSlots[index].positionName = e.value;
                  setTimeSlots(newSlots);
                },
                placeholder: t("sheets.positionName"),
              }}
              validationRules={[
                {
                  type: "required",
                  message: t("sheets.schemaNameRequired"),
                },
              ]}
            />
            <Item
              colSpan={isMobile ? 1 : 2}
              label={{ text: t("sheets.description") }}
              editorType="dxTextArea"
              editorOptions={{
                value: slot.details,
                onValueChanged: (e: any) => {
                  const newSlots = [...timeSlots];
                  newSlots[index].details = e.value;
                  setTimeSlots(newSlots);
                },
                placeholder: t("sheets.description"),
                height: 80,
              }}
            />
            <Item
              label={{ text: t("sheets.slotScales") }}
              editorType="dxNumberBox"
              editorOptions={{
                value: slot.scales,
                showSpinButtons: true,
                format: "#0",
                min: 0,
                onValueChanged: (e) => {
                  const newSlots = [...timeSlots];
                  newSlots[index].scales = e.value;
                  setTimeSlots(newSlots);
                  validateAll();
                },
              }}
            />
          </Form>
        </div>
      ))}
      <Button
        text={t("Add")}
        icon="add"
        type="default"
        onClick={handleAddTimeSlot}
      />
    </>
  );

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("sheets.sheets")}
        </div>
        <div className="mb-1 text-base">{t("sheets.headerText")}</div>

        {isMobile ? (
          <div className="sheets-mobile">
            <div className="sheets-mobile-filter">
              <TextBox
                value={mobileFilter}
                onValueChanged={(e) => setMobileFilter(e.value ?? "")}
                placeholder={t("sheets.Search...")}
                showClearButton
              />
            </div>
            <div className="sheets-mobile-list">
              {filteredSchema.length === 0 ? (
                <div className="sheets-mobile-empty">
                  {t("sheets.noData") !== "sheets.noData"
                    ? t("sheets.noData")
                    : t("No data")}
                </div>
              ) : (
                filteredSchema.map((row: any) => {
                  const disabled = row.isRegistered;
                  return (
                    <div key={row.id} className="sheets-mobile-card">
                      <div className="sheets-mobile-card-main">
                        <div className="sheets-mobile-card-name">
                          {row.name}
                        </div>
                        <div className="sheets-mobile-card-meta">
                          {row.gradingMethod && <span>{row.gradingMethod}</span>}
                          <span>
                            {t("sheets.sumScales")}: {row.sumScales}
                          </span>
                        </div>
                      </div>
                      <div className="sheets-mobile-card-actions">
                        <Button
                          icon="edit"
                          stylingMode="text"
                          disabled={disabled}
                          hint={t("sheets.edit")}
                          onClick={() => {
                            if (!disabled) togglePopup(row);
                          }}
                        />
                        <Button
                          icon="trash"
                          stylingMode="text"
                          type="danger"
                          disabled={disabled}
                          hint={t("sheets.delete")}
                          onClick={() => {
                            if (!disabled) hadnleDeleteSchema(row);
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <button
              type="button"
              className="sheets-mobile-fab"
              aria-label={t("sheets.addSchema")}
              onClick={addPopup}
            >
              +
            </button>
          </div>
        ) : (
          <DataGrid
            dataSource={schema}
            keyExpr="id"
            showBorders
            allowColumnResizing
            allowColumnReordering
            columnAutoWidth
            searchPanel={{
              visible: true,
              placeholder: t("sheets.Search..."),
            }}
            paging={{
              pageSize: 10,
            }}
            pager={{
              visible: true,
              showInfo: true,
              showNavigationButtons: true,
            }}
          >
            <Toolbar>
              <DataGridItem
                location="before"
                locateInMenu="auto"
                name="searchPanel"
                cssClass="datagrid-search-panel-without-margin-left"
              />
              <DataGridItem location="after" locateInMenu="auto">
                <Button
                  onClick={addPopup}
                  text={t("sheets.addSchema")}
                  icon="add"
                />
              </DataGridItem>
            </Toolbar>
            <FilterRow visible />
            <Column
              dataField="name"
              dataType="string"
              caption={t("sheets.name")}
            />
            <Column
              dataField="sumScales"
              dataType="string"
              caption={t("sheets.sumScales")}
            />
            <Column
              dataField="gradingMethod"
              dataType="string"
              caption={t("sheets.gradingMethod")}
            />
            <Column
              type="buttons"
              caption={t("sheets.actions")}
              buttons={[
                {
                  icon: "edit",
                  onClick: (e) => {
                    if (e.row && !e.row.data.isRegistered) {
                      togglePopup(e.row.data);
                    }
                  },
                  disabled: (e) => (e.row ? e.row.data.isRegistered : true),
                  hint: t("sheets.edit"),
                },
                {
                  icon: "trash",
                  onClick: (e) => {
                    if (e.row && !e.row.data.isRegistered) {
                      hadnleDeleteSchema(e.row.data);
                    }
                  },
                  disabled: (e) => (e.row ? e.row.data.isRegistered : true),
                  hint: t("sheets.delete"),
                },
              ]}
            />
          </DataGrid>
        )}

        {/* Dodawanie */}
        <Popup
          wrapperAttr={{ class: "sheets-popup" }}
          visible={popupVisibleAdd}
          onHiding={() => setPopupVisibleAdd(false)}
          fullScreen={isMobile}
          maxWidth={isMobile ? undefined : 800}
          height={isMobile ? "100%" : "auto"}
          maxHeight={isMobile ? undefined : "80vh"}
          title={t("sheets.createSchema")}
          showCloseButton
          dragEnabled={!isMobile}
        >
          <Form ref={formRef} validationGroup="schemaGroup">
            <GroupItem caption={t("sheets.generalInfo")} colCount={groupColCount}>
              <Item
                dataField="schemaName"
                label={{ text: t("sheets.schemaName") }}
                editorType="dxTextBox"
                editorOptions={{
                  value: schemaName,
                  onValueChanged: (e: any) => setSchemaName(e.value),
                  placeholder: t("sheets.schemaName"),
                }}
                validationRules={[
                  { type: "required", message: t("sheets.schemaNameRequired") },
                ]}
              />
              <Item
                dataField="scales"
                label={{ text: t("sheets.scales") }}
                editorType="dxNumberBox"
                editorOptions={{
                  value: scales,
                  showSpinButtons: true,
                  format: "#0",
                  min: 0,
                  onValueChanged: (e) => {
                    setScales(e.value);
                    validateAll();
                  },
                }}
              />
              <Item
                dataField="selectMode"
                label={{ text: t("sheets.selectMode") }}
                editorType="dxSelectBox"
                editorOptions={{
                  value: selectedMethod,
                  dataSource: [
                    { value: "Metoda 90", label: "Metoda 90" },
                    { value: "Metoda 180", label: "Metoda 180" },
                  ],
                  valueExpr: "value",
                  displayExpr: "label",
                  onValueChanged: (e: any) => setSelectedMethod(e.value),
                  placeholder: t("sheets.selectMode"),
                }}
              />
            </GroupItem>

            <GroupItem caption={t("sheets.questions")} colCount={1}>
              <Item render={renderSlots} />
            </GroupItem>
            <GroupItem>
              <ButtonItem
                buttonOptions={{
                  text: t("sheets.send"),
                  type: "default",
                  width: isMobile ? "100%" : 140,
                  onClick: () => {
                    const result = validateAll();
                    if (result?.isValid) {
                      addSchema().then(() => {
                        setPopupVisibleAdd(false);
                      });
                    }
                  },
                }}
              />
            </GroupItem>
          </Form>
        </Popup>

        {/* Edycja */}
        <Popup
          wrapperAttr={{ class: "sheets-popup" }}
          visible={popupVisibleUpdate}
          onHiding={() => setPopupVisibleUpdate(false)}
          fullScreen={isMobile}
          maxWidth={isMobile ? undefined : 800}
          height={isMobile ? "100%" : "auto"}
          maxHeight={isMobile ? undefined : "80vh"}
          title={t("sheets.updateSchema")}
          showCloseButton
          dragEnabled={!isMobile}
        >
          <Form ref={formRef} validationGroup="schemaGroup">
            <GroupItem caption={t("sheets.generalInfo")} colCount={groupColCount}>
              <Item
                dataField="schemaName"
                label={{ text: t("sheets.schemaName") }}
                editorType="dxTextBox"
                editorOptions={{
                  value: schemaName,
                  onValueChanged: (e: any) => setSchemaName(e.value),
                  placeholder: t("sheets.schemaName"),
                }}
                validationRules={[
                  { type: "required", message: t("sheets.schemaNameRequired") },
                ]}
              />
              <Item
                dataField="scales"
                label={{ text: t("sheets.scales") }}
                editorType="dxNumberBox"
                editorOptions={{
                  value: scales,
                  showSpinButtons: true,
                  format: "#0",
                  min: 0,
                  onValueChanged: (e) => {
                    setScales(e.value);
                    validateAll();
                  },
                }}
              />
              <Item
                dataField="selectMode"
                label={{ text: t("sheets.selectMode") }}
                editorType="dxSelectBox"
                editorOptions={{
                  value: selectedMethod,
                  dataSource: [
                    { value: "Metoda 90", label: "Metoda 90" },
                    { value: "Metoda 180", label: "Metoda 180" },
                  ],
                  valueExpr: "value",
                  displayExpr: "label",
                  onValueChanged: (e: any) => setSelectedMethod(e.value),
                  placeholder: t("sheets.selectMode"),
                }}
              />
            </GroupItem>

            <GroupItem caption={t("sheets.questions")} colCount={1}>
              <Item render={renderSlots} />
            </GroupItem>

            <GroupItem>
              <ButtonItem
                buttonOptions={{
                  text: t("sheets.update"),
                  type: "default",
                  width: isMobile ? "100%" : 140,
                  onClick: () => {
                    const result = validateAll();
                    if (result?.isValid) {
                      updateSchemaSend().then(() => {
                        setPopupVisibleUpdate(false);
                      });
                    }
                  },
                }}
              />
            </GroupItem>
          </Form>
        </Popup>
      </div>
    </ScrollView>
  );
};
